import { createRoute, Link, useNavigate } from "@tanstack/solid-router";
import { msg } from "@lingui/core/macro";
import { For, Show } from "solid-js";
import { PenLine, Star } from "@fleet/icons";
import { useFavoriteEntriesQuery } from "@journal/shared/solid";
import { appRoute } from "./route";
import { PageContainer } from "@fleet/ui";
import { EmptyState } from "@/components/ui/empty-state";
import { StarredIllustration } from "@/components/ui/illustrations";
import { buttonVariants } from "@/components/ui/button";
import { useI18n } from "@/providers/i18n-provider";
import { EntryCard } from "@/features/journal/components/entry-card";
import { useJournalData } from "@/features/journal/hooks/use-journal-data";

function FavoritesPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { db, drizzle, userId, staleTime } = useJournalData();
  const favorites = useFavoriteEntriesQuery({ db, drizzle, userId, staleTime });

  return (
    <PageContainer maxWidth="2xl" class="flex flex-col gap-4 pb-16">
      <h1 class="pt-2 text-xl font-bold text-foreground">{t(msg`Favorites`)}</h1>
      <Show
        when={(favorites.data ?? []).length > 0}
        fallback={
          <Show when={!favorites.isLoading}>
            <EmptyState
              icon={Star}
              illustration={<StarredIllustration />}
              title={t(msg`No favorites yet`)}
              description={t(msg`Star the entries you want to revisit often.`)}
              onIconActivate={() => {
                void navigate({ to: "/" });
              }}
              iconActionLabel={t(msg`Write an entry`)}
              action={
                <Link to="/" class={buttonVariants({ size: "sm" })}>
                  <PenLine class="h-4 w-4" />
                  {t(msg`Write an entry`)}
                </Link>
              }
            />
          </Show>
        }
      >
        <section class="flex flex-col gap-3" data-testid="favorites-list">
          <For each={favorites.data ?? []}>{(entry) => <EntryCard entry={entry} showDate />}</For>
        </section>
      </Show>
    </PageContainer>
  );
}

export const favoritesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/favorites",
  component: FavoritesPage,
});
